import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { Tab } from "@mui/material";
import { useEffect, useRef } from "react";
import { isMobile, isTablet } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { ActionMenuItem, BaseActionEnum } from "../../components/action-menu/ActionMenuItem";
import AlarmSetpointList, { AlarmSetpointListType } from "../../components/alarm-setpoint-list/AlarmSetpointList";
import { ContentView } from "../../components/content-view/ContentView";
import ContextualAppBar from "../../components/contextual-app-bar/ContextualAppBar";
import ObjectAttachments, { ObjectAttachmentsType } from "../../components/object-attachments/ObjectAttachments";
import ObjectDefaults, { ObjectDefaultsType } from "../../components/object-defaults/ObjectDefaults";
import ObjectProperties, { ObjectPropertiesType } from "../../components/object-properties/ObjectProperties";
import ObjectTagValues, { ObjectTagValuesMode, ObjectTagValuesType } from "../../components/object-tag-values/ObjectTagValues";
import RunTicketList, { RunTicketListType } from "../../components/run-ticket-list/RunTicketList";
import TabBar from "../../components/tab-bar/TabBar";
import TabContainer from "../../components/tab-container/TabContainer";
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import { useAvailableObjectDetailsTabs } from "../../queries/available-views/UseAvailableObjectDetailsTabs";
import { ObjectDetailsViewTabEnum, useObjectDetailsViewState } from "../../states/object-details-view-state/ObjectDetailsViewState";
import ObjectRelationships, { ObjectRelationshipsType } from "../../components/object-relationships/ObjectRelationships";
import ObjectExternalContent, { ObjectExternalContentType } from "../../components/object-external-content/ObjectExternalContent";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { useObjectViewState } from "../../queries/object-view-state/UseObjectViewState";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { ObjectFeatures2Enum } from "../../common/enums/ObjectFeatures2Enum";
import ObjectHistoryContextProvider from "../../components/object-history/ObjectHistoryContext";
import ObjectHistoryTabContent, { ObjectHistoryTabContentType } from "../../components/object-history/ObjectHistoryTabContent";
import useMapTo from "../../hooks/use-map-to/useMapTo";
import { ConfigurationActionEnum, ConfigurationActionItem } from "../../components/action-menu/ConfigurationActions";
import useNavigateTo from "../../hooks/use-navigate-to/useNavigateTo";
import { getDeviceRoute } from "../../common/utilities/RouteHelpers";
import { UIViewEnum } from "../../common/enums/UIViewEnum";

export default function DeviceDetailsView() {

    // console.log("========== Rendering DeviceDetailsView ==========");

    const objectTagValues = useRef<ObjectTagValuesType>(undefined);
    const runTicketList = useRef<RunTicketListType>(undefined);
    const alarmSetpointList = useRef<AlarmSetpointListType>(undefined);
    const objectHistory = useRef<ObjectHistoryTabContentType>(null);
    const objectDefaults = useRef<ObjectDefaultsType>(undefined);
    const objectAttachments = useRef<ObjectAttachmentsType>(undefined);
    const objectRelationships = useRef<ObjectRelationshipsType>(undefined);
    const objectExternalContent = useRef<ObjectExternalContentType>(undefined);
    const objectProperties = useRef<ObjectPropertiesType>(undefined);
    const isPhone = isMobile && !isTablet;
    const [offlineState] = useOfflineDetectContext();
    const routeContext = useRouteContext();
    const [userState] = useUserContext();
    const mapTo = useMapTo();
    const { t } = useTranslation();
    const navigateTo = useNavigateTo();



    const {
        //deviceType,
        activeTab,
        setActiveTab,
        actions,
        setActions,
        resetActions,
        viewOptionState,
        setViewOptionState,
        setHasAlarmHistory,
        setHasSettingsChanges,
        latitude,
        longitude,
        configurationActions,
        setConfigurationsActions
    } = useObjectDetailsViewState(eLynxObjectTypeEnum.Device/*, configurationEnabled*/);

    const { data: availableTabs } = useAvailableObjectDetailsTabs({ enabled: true });


    const { data: objectViewState } = useQueryNotifications(useObjectViewState)({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number,
        contextObject: {
            id: routeContext.contextObject?.id as number,
            objectType: routeContext.contextObject?.objectType as eLynxObjectTypeEnum
        },
        offline: offlineState.offline,
        enabled: !offlineState.offline
    });

    const hasExternalConent = ((objectViewState?.features2 ?? ObjectFeatures2Enum.None) & ObjectFeatures2Enum.AdditionalContent) > 0 &&
        objectViewState?.externalContent?.url;


    function onManageComm() {

        const url = `${getDeviceRoute(UIViewEnum.CommManagement,
            routeContext.groupID,
            routeContext.wellID,
            routeContext.deviceID)}&manageObject=true`;

        navigateTo(url, undefined);
    }

    function onExecuteAction(action: ActionMenuItem) {

        const item = action as ActionMenuItem | ConfigurationActionItem;

        switch (item.action) {

            case BaseActionEnum.Refresh:

                if (activeTab === ObjectDetailsViewTabEnum.Values) {
                    objectTagValues.current?.executeAction(item.action);
                } else if (activeTab === ObjectDetailsViewTabEnum.History) {
                    objectHistory.current?.executeAction(item.action);
                } else if (activeTab === ObjectDetailsViewTabEnum.RunTickets) {
                    runTicketList.current?.executeAction(item.action);
                } else if (activeTab === ObjectDetailsViewTabEnum.AlarmSetpoints) {
                    alarmSetpointList.current?.executeAction(item.action);
                } else if (!isPhone && !offlineState.offline && activeTab === ObjectDetailsViewTabEnum.Defaults) {
                    objectDefaults.current?.executeAction(item.action);
                } else if (!isPhone && !offlineState.offline && activeTab === ObjectDetailsViewTabEnum.Attachments) {
                    objectAttachments.current?.executeAction(item.action);
                } else if (!isPhone && !offlineState.offline && activeTab === ObjectDetailsViewTabEnum.Relationships) {
                    objectRelationships.current?.executeAction(item.action);
                }

                break;

            case BaseActionEnum.DemandScan:
            case BaseActionEnum.WriteDown:
                setActiveTab(ObjectDetailsViewTabEnum.Values);
                objectTagValues.current?.executeAction(item.action);
                break;

            case BaseActionEnum.AddMemo:
            case BaseActionEnum.AddDiagnositicEvent:
            case BaseActionEnum.AddWaterLossEvent:
                setActiveTab(ObjectDetailsViewTabEnum.History);
                objectHistory.current?.executeAction(item.action);
                break;

            case BaseActionEnum.AddRunTicket:
                setActiveTab(ObjectDetailsViewTabEnum.RunTickets);
                runTicketList.current?.executeAction(item.action);
                break;

            case BaseActionEnum.AddAttachment:
                setActiveTab(ObjectDetailsViewTabEnum.Attachments);
                objectAttachments.current?.executeAction(item.action);
                break;

            case BaseActionEnum.AddAttribute:
            case BaseActionEnum.ModifyPollingSchedule:                
                setActiveTab(ObjectDetailsViewTabEnum.Properties);
                objectProperties.current?.executeAction(item.action);
                break;


            case BaseActionEnum.Excel:
            case BaseActionEnum.PDF:
                if (activeTab === ObjectDetailsViewTabEnum.Values) {
                    objectTagValues.current?.executeAction(item.action);
                } else if (activeTab === ObjectDetailsViewTabEnum.History) {
                    objectHistory.current?.executeAction(item.action);
                } else if (activeTab === ObjectDetailsViewTabEnum.RunTickets) {
                    runTicketList.current?.executeAction(item.action);
                }
                break;

            case BaseActionEnum.MapIt: 

                if (latitude && longitude) {
                    mapTo({
                        latitude, 
                        longitude
                    });
                }

                break;

            case BaseActionEnum.ManageComm:
                onManageComm();
                break;

            case BaseActionEnum.DisableAllAlarms:
            case BaseActionEnum.EnableAllAlarms:
                setActiveTab(ObjectDetailsViewTabEnum.AlarmSetpoints);
                alarmSetpointList.current?.executeAction(item.action);
                break;

            case ConfigurationActionEnum.CreateNew:
            case ConfigurationActionEnum.Edit:
                setActiveTab(ObjectDetailsViewTabEnum.Values);
                objectTagValues.current?.executeAction(item.action);
                break;

                
            case ConfigurationActionEnum.ModifyPermissions:
            case ConfigurationActionEnum.SetAsDefault:
            case ConfigurationActionEnum.Duplicate:
            case ConfigurationActionEnum.Rename:
            case ConfigurationActionEnum.Delete:
                if(activeTab === ObjectDetailsViewTabEnum.Values) {
                    objectTagValues.current?.executeAction(item.action);
                }
                break;
        }
    }


    useEffect(() => {
        if (sessionStorage.getItem('addmemo') != null && objectHistory.current) {
            objectHistory.current.executeAction(BaseActionEnum.AddMemo);
            sessionStorage.removeItem('addmemo');
        }
    });

    return (
        <ContentView>

            <ContextualAppBar 
                actions={actions}
                configurationActions={configurationActions}
                viewOptionState={viewOptionState}
                onExecuteAction={onExecuteAction}
            />

            <TabBar
                value={activeTab}
                onChange={(_event, value) => setActiveTab(value)}
            >
                {availableTabs?.hasValues &&
                    <Tab label={t("objectDetails.tabs.tagValues")} value={ObjectDetailsViewTabEnum.Values} />
                }

                {availableTabs?.hasProperties &&
                    <Tab label={t("objectDetails.tabs.properties")} value={ObjectDetailsViewTabEnum.Properties} />
                }

                {!isPhone && !offlineState.offline && availableTabs?.hasDefaults &&
                    <Tab label={t("objectDetails.tabs.defaults")} value={ObjectDetailsViewTabEnum.Defaults} />
                }

                {availableTabs?.hasAlarmSetpoints &&
                    <Tab label={t("objectDetails.tabs.alarmSetpoints")} value={ObjectDetailsViewTabEnum.AlarmSetpoints} />
                }

                {/*!isPhone && */!offlineState.offline && availableTabs?.hasAttachments &&
                    <Tab label={t("objectDetails.tabs.attachments")} value={ObjectDetailsViewTabEnum.Attachments} />
                }

                {availableTabs?.hasHistory &&
                    <Tab label={t("objectDetails.tabs.history")} value={ObjectDetailsViewTabEnum.History} />
                }

                {availableTabs?.hasRunTickets &&
                    <Tab label={t("objectDetails.tabs.runTickets")} value={ObjectDetailsViewTabEnum.RunTickets} />
                }

                {!isPhone && !offlineState.offline && availableTabs?.hasRelationships &&
                    <Tab label={t("objectDetails.tabs.relationships")} value={ObjectDetailsViewTabEnum.Relationships} />
                }

                {hasExternalConent &&
                    <Tab label={objectViewState?.externalContent?.title ?? ''} value={ObjectDetailsViewTabEnum.ExternalContent} />
                }

            </TabBar>

            {availableTabs?.hasValues &&
                <TabContainer visible={activeTab === ObjectDetailsViewTabEnum.Values} >
                    <ObjectTagValues
                        mode={ObjectTagValuesMode.Values}
                        grouped={true}
                        actions={actions}
                        setActions={setActions}
                        resetActions={resetActions}
                        setViewOptionState={setViewOptionState}
                        ref={objectTagValues}
                        setConfigurationActions={setConfigurationsActions}
                    />

                </TabContainer>
            }

            {availableTabs?.hasProperties &&
                <TabContainer visible={activeTab === ObjectDetailsViewTabEnum.Properties} >
                    <ObjectProperties
                        ref={objectProperties} />
                </TabContainer>
            }

            {!isPhone && !offlineState.offline && availableTabs?.hasDefaults &&
                <TabContainer visible={activeTab === ObjectDetailsViewTabEnum.Defaults}>
                    <ObjectDefaults
                        visible={activeTab === ObjectDetailsViewTabEnum.Defaults}
                        setViewOptionState={setViewOptionState}
                        ref={objectDefaults}
                    />
                </TabContainer>
            }

            {availableTabs?.hasAlarmSetpoints &&
                <TabContainer visible={activeTab === ObjectDetailsViewTabEnum.AlarmSetpoints} >
                    <AlarmSetpointList
                        ref={alarmSetpointList}
                        setViewOptionState={setViewOptionState}
                    />
                </TabContainer>
            }

            {/*!isPhone && */!offlineState.offline && availableTabs?.hasAttachments &&
                <TabContainer visible={activeTab === ObjectDetailsViewTabEnum.Attachments}>
                    <ObjectAttachments
                        setViewOptionState={setViewOptionState}
                        ref={objectAttachments}
                    />
                </TabContainer>
            }

            {availableTabs?.hasHistory &&
                <ObjectHistoryContextProvider>
                    <TabContainer visible={activeTab === ObjectDetailsViewTabEnum.History} >
                        <ObjectHistoryTabContent
                            ref={objectHistory}
                            setViewOptionState={setViewOptionState}
                            hasAlarmHistory={(hasAlarmHistory: boolean) => setHasAlarmHistory(hasAlarmHistory)}
                            hasSettingsChanges={(hasSettingsChanges: boolean) => setHasSettingsChanges(hasSettingsChanges)}
                        />
                    </TabContainer>
                </ObjectHistoryContextProvider>
            }

            {availableTabs?.hasRunTickets &&
                <TabContainer visible={activeTab === ObjectDetailsViewTabEnum.RunTickets} >
                    <RunTicketList
                        ref={runTicketList}
                        setViewOptionState={setViewOptionState}
                    />
                </TabContainer>
            }

            {availableTabs?.hasRelationships &&
                <TabContainer visible={activeTab === ObjectDetailsViewTabEnum.Relationships}>
                    <ObjectRelationships
                        ref={objectRelationships}
                        setViewOptionState={setViewOptionState}
                    />
                </TabContainer>
            }

            {hasExternalConent &&
                <TabContainer visible={activeTab === ObjectDetailsViewTabEnum.ExternalContent}>
                    <ObjectExternalContent
                        ref={objectExternalContent}
                    />
                </TabContainer>
            }


        </ContentView>
    );
}

